/**
 * Admin Shelly Blacklist Management
 * UI for managing blacklisted Shelly buttons - Admin only
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Ban, Radio, ChevronLeft, RefreshCw, Plus,
  Trash2, AlertTriangle, Calendar, User, Shield,
  Search, X, Check
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';

// Blacklist entry row component
const BlacklistRow = ({ entry, onRemove, isRemoving }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="bg-card rounded-xl border border-red-500/30 p-4"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0">
            <Ban className="w-6 h-6 text-red-500" />
          </div>
          
          <div>
            <div className="flex items-center gap-2 mb-1">
              <p className="font-bold text-lg">{entry.device_id}</p>
              <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                Bloqueado
              </span>
            </div>
            
            <div className="space-y-1 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <AlertTriangle className="w-4 h-4" />
                <span className="font-medium">Razón:</span>
                <span>{entry.reason || 'Sin razón especificada'}</span>
              </div>
              
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="w-4 h-4" />
                <span className="font-medium">Fecha de bloqueo:</span>
                <span>{formatDate(entry.reported_at)}</span>
              </div>
              
              <div className="flex items-center gap-2 text-muted-foreground">
                <User className="w-4 h-4" />
                <span className="font-medium">Bloqueado por:</span>
                <span>{entry.reported_by_email || 'Admin'}</span>
              </div>
            </div>
          </div>
        </div>
        
        <Button
          variant="destructive"
          size="sm"
          onClick={() => onRemove(entry.device_id)}
          disabled={isRemoving}
          className="flex-shrink-0"
        >
          {isRemoving ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Trash2 className="w-4 h-4 mr-2" />
              Desbloquear
            </>
          )}
        </Button>
      </div>
    </motion.div>
  );
};

export default function AdminShellyBlacklist() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [blacklist, setBlacklist] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [removingId, setRemovingId] = useState(null);
  
  // Add to blacklist dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addForm, setAddForm] = useState({ device_id: '', reason: '' });
  const [addLoading, setAddLoading] = useState(false);

  const fetchBlacklist = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getBlacklist();
      setBlacklist(response.data.blacklist || []);
    } catch (err) {
      console.error('Error fetching blacklist:', err);
      toast.error('Error al cargar la lista negra');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBlacklist();
  }, [fetchBlacklist]);

  const handleRemoveFromBlacklist = async (deviceId) => {
    if (!confirm(`¿Estás seguro de que quieres desbloquear el dispositivo ${deviceId}?`)) {
      return;
    }

    try {
      setRemovingId(deviceId);
      await adminAPI.removeFromBlacklist(deviceId);
      toast.success('Dispositivo desbloqueado correctamente');
      fetchBlacklist();
    } catch (err) {
      console.error('Error removing from blacklist:', err);
      toast.error('Error al desbloquear el dispositivo');
    } finally {
      setRemovingId(null);
    }
  };

  const handleAddToBlacklist = async (e) => {
    e.preventDefault();
    
    if (!addForm.device_id.trim()) {
      toast.error('El ID del dispositivo es requerido');
      return;
    }
    
    if (!addForm.reason.trim()) {
      toast.error('La razón del bloqueo es requerida');
      return;
    }

    try {
      setAddLoading(true);
      await adminAPI.addToBlacklist(addForm);
      toast.success('Dispositivo agregado a la lista negra');
      setShowAddDialog(false);
      setAddForm({ device_id: '', reason: '' });
      fetchBlacklist();
    } catch (err) {
      console.error('Error adding to blacklist:', err);
      const errorMsg = err.response?.data?.detail || 'Error al agregar a la lista negra';
      toast.error(errorMsg);
    } finally {
      setAddLoading(false);
    }
  };

  // Filter blacklist by search term
  const filteredBlacklist = blacklist.filter(entry => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      entry.device_id?.toLowerCase().includes(search) ||
      entry.reason?.toLowerCase().includes(search) ||
      entry.reported_by_email?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between p-4 max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/shelly')}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-display font-bold text-lg flex items-center gap-2">
                <Ban className="w-5 h-5 text-red-500" />
                Lista Negra de Botones Shelly
              </h1>
              <p className="text-xs text-muted-foreground">Administrar dispositivos bloqueados</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchBlacklist} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
            <Button size="sm" onClick={() => setShowAddDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Agregar
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="bg-red-500/10 border-red-500/30">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-red-400 mb-1">Información Importante</h3>
                  <p className="text-sm text-muted-foreground">
                    Los dispositivos en esta lista están permanentemente bloqueados y no pueden:
                  </p>
                  <ul className="list-disc list-inside text-sm text-muted-foreground mt-2 space-y-1">
                    <li>Generar clips de replay</li>
                    <li>Recibir webhooks del servidor</li>
                    <li>Ser asignados a usuarios o canchas</li>
                  </ul>
                  <p className="text-sm text-muted-foreground mt-2">
                    <strong className="text-yellow-400">Solo los administradores</strong> pueden desbloquear dispositivos.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-3 gap-4"
        >
          <Card className="bg-card">
            <CardContent className="p-4 text-center">
              <Ban className="w-8 h-8 mx-auto text-red-500 mb-2" />
              <p className="text-3xl font-bold">{blacklist.length}</p>
              <p className="text-sm text-muted-foreground">Dispositivos Bloqueados</p>
            </CardContent>
          </Card>
          
          <Card className="bg-card">
            <CardContent className="p-4 text-center">
              <Radio className="w-8 h-8 mx-auto text-blue-500 mb-2" />
              <p className="text-3xl font-bold">
                {blacklist.filter(b => b.reported_at && new Date(b.reported_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length}
              </p>
              <p className="text-sm text-muted-foreground">Bloqueados (7 días)</p>
            </CardContent>
          </Card>
          
          <Card className="bg-card md:col-span-1 col-span-2">
            <CardContent className="p-4 text-center">
              <AlertTriangle className="w-8 h-8 mx-auto text-yellow-500 mb-2" />
              <p className="text-sm text-muted-foreground">Razón más común:</p>
              <p className="font-medium truncate">
                {blacklist.length > 0 
                  ? (() => {
                      const counts = blacklist.reduce((acc, curr) => {
                        acc[curr.reason] = (acc[curr.reason] || 0) + 1;
                        return acc;
                      }, {});
                      const entries = Object.entries(counts);
                      entries.sort((a, b) => b[1] - a[1]);
                      return entries[0]?.[0] || 'N/A';
                    })()
                  : 'N/A'}
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por ID, razón o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </motion.div>

        {/* Blacklist */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-3"
        >
          <h2 className="font-display font-bold text-lg">
            Dispositivos Bloqueados ({filteredBlacklist.length})
          </h2>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredBlacklist.length === 0 ? (
            <Card className="bg-card">
              <CardContent className="p-12 text-center">
                <Check className="w-12 h-12 mx-auto text-green-500 mb-4" />
                <p className="text-lg font-medium">
                  {searchTerm ? 'No se encontraron resultados' : 'No hay dispositivos bloqueados'}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {searchTerm ? 'Intenta con otro término de búsqueda' : 'La lista negra está vacía'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <AnimatePresence>
              {filteredBlacklist.map((entry) => (
                <BlacklistRow
                  key={entry.device_id}
                  entry={entry}
                  onRemove={handleRemoveFromBlacklist}
                  isRemoving={removingId === entry.device_id}
                />
              ))}
            </AnimatePresence>
          )}
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex flex-wrap gap-3 pt-4"
        >
          <Button variant="outline" onClick={() => navigate('/admin/shelly')}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Volver a Botones Shelly
          </Button>
          <Button variant="outline" onClick={() => navigate('/admin')}>
            Dashboard Admin
          </Button>
        </motion.div>
      </div>

      {/* Add to Blacklist Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-500" />
              Agregar a Lista Negra
            </DialogTitle>
            <DialogDescription>
              Bloquear un dispositivo Shelly para que no pueda generar clips ni recibir eventos.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAddToBlacklist} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="device_id">ID del Dispositivo *</Label>
              <Input
                id="device_id"
                placeholder="ej: SHELLY-ABC123"
                value={addForm.device_id}
                onChange={(e) => setAddForm({ ...addForm, device_id: e.target.value })}
                required
              />
              <p className="text-xs text-muted-foreground">
                El ID único del botón Shelly que deseas bloquear
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="reason">Razón del Bloqueo *</Label>
              <Input
                id="reason"
                placeholder="ej: Uso indebido, spam de clips, dispositivo robado..."
                value={addForm.reason}
                onChange={(e) => setAddForm({ ...addForm, reason: e.target.value })}
                required
              />
              <p className="text-xs text-muted-foreground">
                Motivo por el cual se está bloqueando este dispositivo
              </p>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="destructive" disabled={addLoading}>
                {addLoading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Ban className="w-4 h-4 mr-2" />
                )}
                Bloquear Dispositivo
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
